/**
 * Image attachments for the AI composer. Pure helpers + types shared by the
 * cross-shell store (which owns the pending list + object-URL lifecycle) and
 * the Composer (which validates dropped/pasted/picked files and renders
 * previews). Kept here — not in the component — so the validation is unit
 * testable and the store can hold the type without importing UI.
 */

/** Allowlist the model (Claude, multimodal) accepts as image content blocks. */
export const ACCEPTED_IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
] as const;

/**
 * Spreadsheet types accepted for the AI guest-import flow. These are NOT sent
 * to the model as content blocks — they ride only via the store's
 * `turnAttachments` and are parsed in-browser by the import client tools. The
 * browser's reported MIME for .xlsx/.csv is unreliable, so the Composer also
 * accepts by extension (see ACCEPTED_SPREADSHEET_EXTENSIONS).
 */
export const ACCEPTED_SPREADSHEET_TYPES = [
  "text/csv",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
] as const;

export const ACCEPTED_SPREADSHEET_EXTENSIONS = [".csv", ".xlsx", ".xls"] as const;

/** Hard caps — image cap is mirrored by the backend validation on AiChatRequest.images. */
export const MAX_ATTACHMENTS = 4;
export const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024; // 5 MB
/** Spreadsheets stay in the browser (parsed locally) so a larger cap is safe. */
export const MAX_SPREADSHEET_BYTES = 10 * 1024 * 1024; // 10 MB

export type AttachmentKind = "image" | "spreadsheet";

/**
 * One pending attachment held in the store. The object URL is minted when the
 * file is added and revoked on remove/clear (no leaks) — see ai-store.ts.
 */
export type ComposerAttachment = {
  id: string;
  file: File;
  /** object URL for the preview thumbnail; "" only in non-DOM (test) paths. */
  url: string;
};

/**
 * Base64 image block as it travels to the model — the wire shape the provider
 * threads into `streamAiChat({ images })` and the backend turns into an
 * Anthropic `{type:"image",source:{type:"base64",…}}` content block.
 */
export type ImageAttachment = {
  mediaType: string;
  /** base64 payload WITHOUT the `data:…;base64,` prefix. */
  data: string;
};

export type AttachErrorKind = "badType" | "tooLarge" | "tooMany";

export type RejectedFile = { kind: AttachErrorKind; name: string };

export type ValidationResult = {
  accepted: File[];
  rejected: RejectedFile[];
};

export function isAcceptedImageType(type: string): boolean {
  return (ACCEPTED_IMAGE_TYPES as readonly string[]).includes(type);
}

function hasSpreadsheetExtension(name: string): boolean {
  const lower = name.toLowerCase();
  return (ACCEPTED_SPREADSHEET_EXTENSIONS as readonly string[]).some((ext) =>
    lower.endsWith(ext),
  );
}

/** A spreadsheet by MIME type OR by .csv/.xlsx/.xls extension (MIME is flaky). */
export function isAcceptedSpreadsheet(file: File): boolean {
  return (
    (ACCEPTED_SPREADSHEET_TYPES as readonly string[]).includes(file.type) ||
    hasSpreadsheetExtension(file.name)
  );
}

/** Classify an attachment, or null if it's neither an accepted image nor sheet. */
export function attachmentKind(file: File): AttachmentKind | null {
  if (isAcceptedImageType(file.type)) return "image";
  if (isAcceptedSpreadsheet(file)) return "spreadsheet";
  return null;
}

/**
 * Splits incoming files into the ones that may be attached and the rejections
 * (with a reason). Respects the remaining slot count so a drop that overflows
 * `MAX_ATTACHMENTS` reports `tooMany` for the excess instead of silently
 * dropping it. Order is preserved; the count check runs LAST so a too-large or
 * bad-type file never burns a slot.
 */
export function validateFiles(files: File[], existingCount: number): ValidationResult {
  const accepted: File[] = [];
  const rejected: RejectedFile[] = [];
  let slots = Math.max(0, MAX_ATTACHMENTS - existingCount);
  for (const file of files) {
    const kind = attachmentKind(file);
    if (kind === null) {
      rejected.push({ kind: "badType", name: file.name });
      continue;
    }
    const maxBytes =
      kind === "spreadsheet" ? MAX_SPREADSHEET_BYTES : MAX_ATTACHMENT_BYTES;
    if (file.size > maxBytes) {
      rejected.push({ kind: "tooLarge", name: file.name });
      continue;
    }
    if (slots <= 0) {
      rejected.push({ kind: "tooMany", name: file.name });
      continue;
    }
    accepted.push(file);
    slots--;
  }
  return { accepted, rejected };
}

/** Claude's optimal long edge — larger images are downscaled before upload
 *  (a full-size photo costs multiples in upload bytes AND image tokens for
 *  zero model-accuracy gain). */
export const MAX_IMAGE_EDGE_PX = 1568;

/**
 * Wire budget for ALL images of one chat turn, in source bytes.
 *
 * The origin proxy in front of the API rejects request bodies over 1 MiB with
 * a bare nginx 413 — an error page that carries no CORS headers, so the
 * browser reports it as a CORS failure, `fetch` throws before any status is
 * readable, and the chat shows the generic "assistant unavailable". A pasted
 * retina screenshot cleared that wall easily: 1568px is only the PIXEL cap,
 * a UI screenshot re-encoded as PNG stays megabytes, and base64 adds another
 * third on top.
 *
 * 600 KB of image bytes → ~800 KB of base64, which leaves the JSON envelope,
 * the message text (backend caps it at 8000 chars) and the remaining fields
 * ~200 KB of headroom under the wall.
 */
export const MAX_TURN_IMAGE_BYTES = 600 * 1024;

/**
 * Re-encode ladder, walked until the output fits the budget. Step 1 is the
 * "good screenshot" setting; the later steps trade pixels for bytes rather
 * than let the turn die at the proxy.
 */
const ENCODE_LADDER = [
  { edge: MAX_IMAGE_EDGE_PX, quality: 0.85 },
  { edge: MAX_IMAGE_EDGE_PX, quality: 0.7 },
  { edge: 1280, quality: 0.7 },
  { edge: 1024, quality: 0.6 },
  { edge: 800, quality: 0.5 },
] as const;

/** Whether canvas can ENCODE WebP (decoding is universal, encoding is not —
 *  a browser that can't silently hands back a PNG from toBlob). Probed per
 *  image rather than cached: a 1×1 toDataURL is negligible next to the
 *  re-encode it guards, and a cached answer would leak across tests. */
function canEncodeWebp(): boolean {
  try {
    const probe = document.createElement("canvas");
    probe.width = 1;
    probe.height = 1;
    return probe.toDataURL("image/webp").startsWith("data:image/webp");
  } catch {
    return false;
  }
}

/** WebP for sources that may carry transparency (PNG/GIF) — it keeps the
 *  alpha channel at a fraction of PNG's size, which is exactly the case that
 *  used to blow the budget; JPEG for photographic sources and as the fallback
 *  when the browser cannot encode WebP. */
function encodeType(file: File): string {
  const alphaCapable = file.type === "image/png" || file.type === "image/gif";
  return alphaCapable && canEncodeWebp() ? "image/webp" : "image/jpeg";
}

function renderToBlob(
  bitmap: ImageBitmap,
  scale: number,
  type: string,
  quality: number,
): Promise<Blob | null> {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) return Promise.resolve(null);
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, quality));
}

/**
 * Shrink an image until it fits `budgetBytes` (and the pixel cap):
 * - anything already inside BOTH caps passes through byte-identical (no
 *   re-encode — a small PNG keeps its transparency and an animated GIF keeps
 *   its animation);
 * - everything else walks {@link ENCODE_LADDER} and stops at the first output
 *   inside the budget. An oversized GIF loses its animation here on purpose:
 *   a still frame the model can see beats a turn the proxy rejects;
 * - ANY decode/canvas failure falls back to the original file — an exotic
 *   image must degrade to "sent full size", never to a broken attachment.
 */
async function encodeWithinBudget(file: File, budgetBytes: number): Promise<Blob> {
  const fitsBudget = file.size <= budgetBytes;
  if (file.type === "image/gif" && fitsBudget) return file;
  try {
    // Pin EXIF handling: pre-rotated bitmap with swapped dimensions, so the
    // drawImage below is orientation-correct on every browser.
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    try {
      const longEdge = Math.max(bitmap.width, bitmap.height);
      if (!longEdge) return file;
      if (fitsBudget && longEdge <= MAX_IMAGE_EDGE_PX) return file;
      const outType = encodeType(file);
      let best: Blob | null = null;
      for (const step of ENCODE_LADDER) {
        // Never upscale: a small-but-heavy image is re-encoded at 1:1.
        const scale = Math.min(1, step.edge / longEdge);
        const blob = await renderToBlob(bitmap, scale, outType, step.quality);
        if (!blob) break;
        best = blob;
        if (blob.size <= budgetBytes) break;
      }
      return best ?? file;
    } finally {
      bitmap.close();
    }
  } catch {
    return file;
  }
}

/**
 * Reads a File into the base64 wire block the model consumes, re-encoding it
 * to fit `budgetBytes` first. Rejects with `attachTooLarge` when even the
 * bottom of the ladder does not fit (or the re-encode never ran because the
 * decode failed) — a named rejection the composer can surface beats a body
 * the proxy answers with an opaque 413.
 */
export async function fileToImageAttachment(
  file: File,
  budgetBytes: number = MAX_TURN_IMAGE_BYTES,
): Promise<ImageAttachment> {
  const source = await encodeWithinBudget(file, budgetBytes);
  if (source.size > budgetBytes) {
    throw new Error("attachTooLarge");
  }
  const dataUrl = await readAsDataUrl(source);
  const comma = dataUrl.indexOf(",");
  return {
    mediaType: source.type || file.type,
    data: comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl,
  };
}

/**
 * Encodes one turn's images, splitting {@link MAX_TURN_IMAGE_BYTES} evenly
 * across them — the proxy limit applies to the whole request body, so four
 * attachments must share the budget rather than each spend it.
 */
export function filesToImageAttachments(files: File[]): Promise<ImageAttachment[]> {
  const budget = Math.floor(MAX_TURN_IMAGE_BYTES / Math.max(1, files.length));
  return Promise.all(files.map((file) => fileToImageAttachment(file, budget)));
}

function readAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error("attachReadFailed"));
    reader.readAsDataURL(file);
  });
}

/** Image files out of a clipboard/drag payload — non-images are ignored here
 *  (so a plain-text paste falls through to the textarea); the allowlist +
 *  size/count checks run in {@link validateFiles}. */
export function imageFilesFrom(data: DataTransfer | null): File[] {
  if (!data) return [];
  return Array.from(data.files).filter((f) => f.type.startsWith("image/"));
}

/** Attachable files (images + spreadsheets) out of a drag payload — anything
 *  else is ignored. Used by the composer drop target; the per-kind size/count
 *  checks run in {@link validateFiles}. */
export function attachableFilesFrom(data: DataTransfer | null): File[] {
  if (!data) return [];
  return Array.from(data.files).filter((f) => attachmentKind(f) !== null);
}
